// Unpublished Work © 2026 Deere & Company.
import React from 'react';
import IconAlertInfo from '@deere/icons/IconAlertInfo';
import {Button, Tooltip, Typography, useTheme} from '@deere/isg.component-library';
import {translationStore} from '../stores/translation-store';
import {useIsMobile} from '../helpers/use-is-mobile-helper';

const WINDOWS = 'Windows';
const MAC_OS = 'Mac OS';

type SupportedOS = typeof WINDOWS | typeof MAC_OS;

const osToDownloadLinkMapping: Record<SupportedOS, string> = {
    [WINDOWS]: '/download-data-manager/win',
    [MAC_OS]: '/download-data-manager/mac'
};

function isSupportedOS(osName: string | undefined): osName is SupportedOS {
    return osName === WINDOWS || osName === MAC_OS;
}

export const DataManagerPage = ({osName}: {osName: string | undefined}) => {
    const buttonLink = isSupportedOS(osName) ? osToDownloadLinkMapping[osName] : null;
    const shouldShowButton = buttonLink !== null;
    const bullets = [
        translationStore.DATA_MANAGER_DESCRIPTION_LINE_1,
        translationStore.DATA_MANAGER_DESCRIPTION_LINE_2,
        translationStore.DATA_MANAGER_DESCRIPTION_LINE_3,
        translationStore.DATA_MANAGER_DESCRIPTION_LINE_4
    ];
    const theme = useTheme();
    const isMobile = useIsMobile(499);

    return (
        <div className='flex m-4' data-testid='download-page'>
            <div className='bg-white p-4 max-w-[700px] rounded-lg' data-testid='content'>
                <Typography variant='h1'>{translationStore.tool_john_deere_data_manager}</Typography>
                {!shouldShowButton && (
                    <div
                        className={`rounded p-3 mt-4 flex items-center gap-2`}
                        style={{backgroundColor: theme.jddcTokens.JddcColorBackgroundInfoSubtle}}
                        data-testid='unsupported-message'
                    >
                        <IconAlertInfo size={20} color={theme.jddcTokens.JddcColorIconInfo} />
                        <Typography variant='bodyMedium'>
                            {translationStore.DATA_MANAGER_WINDOWS_AND_MAC_ONLY}
                        </Typography>
                    </div>
                )}
                <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-6 mt-4`} data-testid='inner-content'>
                    <div className='flex-1' data-testid='description'>
                        <Typography variant='labelLarge' className='mb-3 font-semibold'>
                            {translationStore.DATA_MANAGER_DESCRIPTION_HEADER}
                        </Typography>
                        {isMobile && (
                            <div className='w-[150px] h-[120px] mx-auto my-4' data-testid='image-container-mobile'>
                                <img
                                    alt={translationStore.DATA_MANAGER_DESCRIPTION_HEADER}
                                    src={'/assets/USBtoMyJD-graphic.png'}
                                    className='w-full h-full object-cover'
                                />
                            </div>
                        )}
                        <ul className='list-disc pl-5 my-2'>
                            {bullets.map((text: string, index: number) => (
                                <li key={`bullet-${index}`} className='mb-1'>
                                    <Typography variant='bodyMedium'>{text}</Typography>
                                </li>
                            ))}
                        </ul>
                        <Typography variant='bodySmall' color={theme.jddcTokens.JddcColorTextSecondary}>
                            {translationStore.DATA_MANAGER_FOOTER}
                        </Typography>
                    </div>
                    {!isMobile && (
                        <div className='w-[150px] h-[120px] shrink-0' data-testid='image-container'>
                            <img
                                alt={translationStore.DATA_MANAGER_DESCRIPTION_HEADER}
                                src={'/assets/USBtoMyJD-graphic.png'}
                                className='w-full h-full object-cover'
                            />
                        </div>
                    )}
                </div>
                {shouldShowButton ?
                    <a className='mt-4 block' href={buttonLink} download data-testid='button-container'>
                        <Button variant='primary'>{translationStore.DATA_MANAGER_DOWNLOAD}</Button>
                    </a>
                :   <div className='mt-4' data-testid='disabled-download-container'>
                        <Tooltip title={translationStore.NOT_COMPATIBLE_TOOLTIP} placement='top' variant='dark'>
                            <span>
                                <Button variant='primary' disabled={true}>
                                    {translationStore.DATA_MANAGER_DOWNLOAD}
                                </Button>
                            </span>
                        </Tooltip>
                    </div>
                }
            </div>
        </div>
    );
};
